import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { WS_INVALIDATED_QUERY_STALE_TIME } from '../constants';
import { SchemaProductDto } from '../generated/apiTypes';
import { dataOrThrow, fetchClient } from '../lib/apiClient';
import { Gwei } from '../lib/gwei';

const transformTokenSymbol = (symbol: string) => {
  if (symbol === 'WETH') {
    return 'ETH';
  }
  if (symbol === 'WBTC') {
    return 'BTC';
  }
  return symbol;
};

export const transformProduct = (product: SchemaProductDto) => {
  return {
    ...product,
    blockNumber: Gwei.toGwei(product.blockNumber),
    lotSize: Gwei.toGwei(product.lotSize),
    makerFee: Gwei.toGwei(product.makerFee),
    maxQuantity: Gwei.toGwei(product.maxQuantity),
    takerFee: Gwei.toGwei(product.takerFee),
    tickSize: Gwei.toGwei(product.tickSize),
    fundingRate1h: Gwei.toGwei(product.fundingRate1h),
    openInterest: Gwei.toGwei(product.openInterest),
    minQuantity: Gwei.toGwei(product.minQuantity),
    baseTokenName: transformTokenSymbol(product.baseTokenName),
    quoteTokenName: transformTokenSymbol(product.quoteTokenName),
    maxOpenInterestUsd: Gwei.toGwei(product.maxOpenInterestUsd),
    maxPositionNotionalUsd: Gwei.toGwei(product.maxPositionNotionalUsd),
    maxLeverage: product.maxLeverage,
    initialMarginRatio: 1 / product.maxLeverage,
    maintenanceMarginRatio: 1 / product.maxLeverage / 2,
    displayTicker: product.displayTicker,
    minPrice: Gwei.toGwei(product.minPrice),
    maxPrice: Gwei.toGwei(product.maxPrice),
    volume24h: Gwei.toGwei(product.volume24h),
  };
};
export type ProductType = ReturnType<typeof transformProduct>;
export const productsQuery = (refetchInterval?: number) =>
  queryOptions({
    queryKey: ['GET', '/v1/product'] as const,
    staleTime: WS_INVALIDATED_QUERY_STALE_TIME,
    queryFn: async ({ queryKey, signal }) => {
      const [method, url] = queryKey;
      const { data } = await fetchClient[method](url, { signal }).then(dataOrThrow);
      return data?.data.map(transformProduct);
    },
    refetchInterval,
  });

export const useProducts = (refetchInterval?: number) => useQuery(productsQuery(refetchInterval));

export type ProductsById = Record<string, ProductType>;
export const useProductsById = (refetchInterval?: number) => {
  const { data: products } = useProducts(refetchInterval);
  return useMemo(
    () =>
      products?.reduce<ProductsById>((acc, product) => {
        acc[product.id] = product;
        return acc;
      }, {}),
    [products]
  );
};
